import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import useLiveValue from '../useLiveValue';
import useMeterField from '../useMeterField';
import { useInView } from '../WidgetMiniPreview';

const DEFAULT_BARS = [4, 7, 3, 8, 5, 6];
const DEFAULT_PIE = [35, 25, 20, 20];
const TRACK_COLOR = 0xd1d5db;
const PIE_PALETTE = [0x2a78d6, 0x1baf7a, 0xeda100, 0xe34948, 0x4a3aa7];
const PCT_LERP = 0.06;
const PCT_EPSILON = 0.003;

const SINGLE_VALUE_VARIANTS = new Set([
    'ring3d', 'sphere3d', 'tower3d', 'cylinder3d', 'cone3d', 'pyramid3d',
    'helix3d', 'crystal3d', 'battery3d', 'wave3d', 'turbine3d', 'solar3d',
    'radar3d', 'orb3d', 'network3d', 'flow3d', 'meter-grid3d',
]);

function pivotBottom(geometry) {
    geometry.translate(0, 0.5, 0);
    return geometry;
}

function standard(colorHex, extra) {
    return new THREE.MeshStandardMaterial({ color: colorHex, ...extra });
}

function buildScene(variant, scene, { data, unit }) {
    const allMeshes = [];
    const dataMeshes = [];
    const add = (mesh, label) => {
        scene.add(mesh);
        allMeshes.push(mesh);
        if (label !== undefined) dataMeshes.push({ mesh, label });
        return mesh;
    };

    switch (variant) {
        case 'bar3d': {
            const bars = data && data.length ? data : DEFAULT_BARS;
            const maxBar = Math.max(...bars, 1);
            const meshes = bars.map((v, i) => {
                const h = Math.max(0.3, (v / maxBar) * 3);
                const mesh = new THREE.Mesh(pivotBottom(new THREE.BoxGeometry(0.5, 1, 0.5)), standard(0x4318ff));
                mesh.position.set((i - (bars.length - 1) / 2) * 0.7, -1.5, 0);
                mesh.scale.y = h;
                mesh.userData.baseH = h;
                mesh.userData.phase = i * 0.9;
                return add(mesh, `Bar ${i + 1}: ${v}`);
            });
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    meshes.forEach((mesh) => {
                        mesh.material.color.copy(color);
                        const wobble = 1 + Math.sin(t * 1.4 + mesh.userData.phase) * 0.03;
                        mesh.scale.y = mesh.userData.baseH * wobble;
                    });
                },
            };
        }

        case 'pie3d': {
            const segments = data && data.length ? data : DEFAULT_PIE;
            const total = segments.reduce((sum, v) => sum + v, 0) || 1;
            let startAngle = 0;
            const meshes = segments.map((v, i) => {
                const angle = (v / total) * Math.PI * 2;
                const mesh = new THREE.Mesh(
                    new THREE.CylinderGeometry(1.3, 1.3, 0.6, 32, 1, false, startAngle, angle),
                    standard(i === 0 ? 0x4318ff : PIE_PALETTE[i % PIE_PALETTE.length]),
                );
                mesh.userData.baseY = 0;
                mesh.userData.phase = i * 1.1;
                const label = `${Math.round((v / total) * 100)}% (${v})`;
                startAngle += angle;
                return add(mesh, label);
            });
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    meshes.forEach((mesh, i) => {
                        if (i === 0) mesh.material.color.copy(color);
                        mesh.position.y = Math.sin(t * 1.2 + mesh.userData.phase) * 0.03;
                    });
                },
            };
        }

        case 'ring3d': {
            add(new THREE.Mesh(new THREE.TorusGeometry(1.2, 0.15, 16, 64), standard(TRACK_COLOR)));
            const arc = new THREE.Mesh(new THREE.TorusGeometry(1.2, 0.17, 16, 64, 0.01), standard(0x4318ff));
            arc.userData.adjustable = true;
            add(arc, '');
            let lastAngle = -1;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const angle = Math.max(0.02, Math.PI * 2 * pct);
                    if (Math.abs(angle - lastAngle) > 0.015) {
                        arc.geometry.dispose();
                        arc.geometry = new THREE.TorusGeometry(1.2, 0.17, 16, 64, angle);
                        lastAngle = angle;
                    }
                    arc.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}%`;
                },
            };
        }

        case 'sphere3d': {
            add(new THREE.Mesh(
                new THREE.SphereGeometry(1.3, 24, 24),
                new THREE.MeshBasicMaterial({ color: 0xd1d5db, wireframe: true, transparent: true, opacity: 0.5 }),
            ));
            const core = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 24), standard(0x4318ff));
            core.userData.adjustable = true;
            add(core, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const s = 0.2 + pct * 1.1;
                    core.scale.setScalar(s);
                    core.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}%`;
                },
            };
        }

        case 'tower3d': {
            const base = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 0.2, 32), standard(TRACK_COLOR));
            base.position.y = -1.5;
            add(base);
            const tower = new THREE.Mesh(pivotBottom(new THREE.CylinderGeometry(0.6, 0.6, 1, 32)), standard(0x4318ff));
            tower.position.y = -1.4;
            tower.userData.adjustable = true;
            add(tower, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    tower.scale.y = Math.max(0.05, pct * 3);
                    tower.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}%`;
                },
            };
        }

        case 'cone3d': {
            add(new THREE.Mesh(
                new THREE.ConeGeometry(1.2, 3, 32),
                new THREE.MeshBasicMaterial({ color: 0xd1d5db, wireframe: true, transparent: true, opacity: 0.4 }),
            ));
            const cone = new THREE.Mesh(pivotBottom(new THREE.ConeGeometry(1.1, 1, 32)), standard(0x4318ff));
            cone.position.y = -1.5;
            cone.userData.adjustable = true;
            add(cone, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    cone.scale.y = Math.max(0.05, pct * 3);
                    cone.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}%`;
                },
            };
        }

        case 'pyramid3d': {
            const levels = 5;
            const blocks = [];
            let y = -1.5;
            for (let i = 0; i < levels; i += 1) {
                const size = 1.4 - i * 0.22;
                const blockH = 0.5;
                const block = new THREE.Mesh(new THREE.BoxGeometry(size, blockH, size), standard(TRACK_COLOR));
                block.position.y = y + blockH / 2;
                add(block, `Level ${i + 1}`);
                blocks.push(block);
                y += blockH;
            }
            let lastLit = -1;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const litLevels = Math.max(1, Math.round(pct * levels));
                    if (litLevels !== lastLit) {
                        blocks.forEach((b, i) => b.material.color.set(i < litLevels ? color : TRACK_COLOR));
                        lastLit = litLevels;
                    } else {
                        blocks.forEach((b, i) => { if (i < litLevels) b.material.color.copy(color); });
                    }
                    const topLit = blocks[Math.min(levels, litLevels) - 1];
                    if (topLit) topLit.material.emissive.copy(color).multiplyScalar(0.15 + Math.sin(t * 3) * 0.1);
                },
            };
        }

        case 'helix3d': {
            const buildCoil = (turns, radius, tubeRadius, opacity) => {
                const points = [];
                for (let i = 0; i <= 80; i += 1) {
                    const tt = (i / 80) * turns;
                    const angle = tt * Math.PI * 2;
                    points.push(new THREE.Vector3(Math.cos(angle) * radius, -1.5 + tt * 0.6, Math.sin(angle) * radius));
                }
                const curve = new THREE.CatmullRomCurve3(points);
                return new THREE.Mesh(
                    new THREE.TubeGeometry(curve, 120, tubeRadius, 8, false),
                    opacity < 1
                        ? new THREE.MeshBasicMaterial({ color: 0xd1d5db, transparent: true, opacity })
                        : standard(0x4318ff),
                );
            };
            add(buildCoil(5, 1, 0.03, 0.4));
            const coil = buildCoil(1.5, 1, 0.1, 1);
            coil.userData.adjustable = true;
            add(coil, '');
            let lastTurns = -1;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const turns = Math.max(0.3, pct * 5);
                    if (Math.abs(turns - lastTurns) > 0.08) {
                        coil.geometry.dispose();
                        coil.geometry = buildCoil(turns, 1, 0.1, 1).geometry;
                        lastTurns = turns;
                    }
                    coil.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}% coverage`;
                },
            };
        }

        case 'crystal3d': {
            const gem = new THREE.Mesh(
                new THREE.IcosahedronGeometry(1, 0),
                standard(0x4318ff, { flatShading: true, metalness: 0.3, roughness: 0.25, emissive: 0x000000 }),
            );
            gem.userData.adjustable = true;
            add(gem, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const scale = 0.55 + pct * 0.85;
                    gem.scale.setScalar(scale);
                    gem.rotation.y = t * 0.5;
                    gem.material.color.copy(color);
                    gem.material.emissive.copy(color).multiplyScalar(0.15 + pct * 0.25);
                    dataMeshes[0].label = `${Math.round(pct * 100)}%`;
                },
            };
        }

        case 'battery3d': {
            const shell = new THREE.Mesh(
                new THREE.BoxGeometry(1.6, 3, 1),
                new THREE.MeshBasicMaterial({ color: 0xd1d5db, wireframe: true, transparent: true, opacity: 0.5 }),
            );
            add(shell);
            const nub = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.25, 0.5), standard(TRACK_COLOR));
            nub.position.y = 1.62;
            add(nub);
            const fill = new THREE.Mesh(pivotBottom(new THREE.BoxGeometry(1.3, 1, 0.7)), standard(0x22c55e));
            fill.position.y = -1.45;
            fill.userData.adjustable = true;
            add(fill, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    fill.scale.y = Math.max(0.06, pct * 2.9);
                    const low = pct < 0.2;
                    fill.material.color.copy(low ? new THREE.Color(0xef4444) : color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}% charged`;
                },
            };
        }

        case 'wave3d': {
            add(new THREE.Mesh(
                new THREE.CylinderGeometry(1.3, 1.3, 3, 32, 1, true),
                new THREE.MeshBasicMaterial({ color: 0xd1d5db, side: THREE.DoubleSide, transparent: true, opacity: 0.3 }),
            ));
            const segs = 24;
            const waveGeom = new THREE.PlaneGeometry(2.4, 2.4, segs, segs);
            waveGeom.rotateX(-Math.PI / 2);
            const wave = new THREE.Mesh(waveGeom, standard(0x38bdf8, { side: THREE.DoubleSide }));
            wave.userData.adjustable = true;
            add(wave, '');
            const posAttr = waveGeom.attributes.position;
            const basePositions = posAttr.array.slice();
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    wave.position.y = -1.5 + Math.max(0.1, pct * 3);
                    for (let i = 0; i < posAttr.count; i += 1) {
                        const x = basePositions[i * 3];
                        const z = basePositions[i * 3 + 2];
                        posAttr.array[i * 3 + 1] = Math.sin(x * 2.2 + t * 1.8) * 0.06 + Math.cos(z * 2.2 + t * 1.3) * 0.06;
                    }
                    posAttr.needsUpdate = true;
                    wave.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}% full`;
                },
            };
        }

        case 'turbine3d': {
            const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.18, 3, 16), standard(0xcbd5e1));
            pole.position.y = -1.4;
            add(pole);
            const nacelle = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.3, 0.3), standard(0x64748b));
            nacelle.position.y = 0.1;
            nacelle.userData.adjustable = true;
            add(nacelle);
            const hub = new THREE.Group();
            hub.position.y = 0.1;
            hub.position.z = 0.2;
            scene.add(hub);
            const bladeMat = standard(0x4318ff);
            [0, 1, 2].forEach((i) => {
                const blade = new THREE.Mesh(pivotBottom(new THREE.ConeGeometry(0.14, 1.15, 8)), bladeMat);
                blade.rotation.z = (i * Math.PI * 2) / 3;
                hub.add(blade);
                allMeshes.push(blade);
            });
            const hubCap = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 12), standard(0x334155));
            hub.add(hubCap);
            allMeshes.push(hubCap);
            dataMeshes.push({ mesh: nacelle, label: '' });
            let lastT = 0;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const dt = Math.max(0, Math.min(0.25, t - lastT));
                    lastT = t;
                    hub.rotation.z += dt * (0.9 + pct * 15);
                    bladeMat.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}% output`;
                },
            };
        }

        case 'solar3d': {
            const cols = 4;
            const rows = 3;
            const panelMat = [];
            const panels = [];
            for (let r = 0; r < rows; r += 1) {
                for (let c = 0; c < cols; c += 1) {
                    const mat = standard(TRACK_COLOR, { emissive: 0x000000 });
                    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.06, 0.4), mat);
                    panel.position.set((c - (cols - 1) / 2) * 0.62, 0, (r - (rows - 1) / 2) * 0.5);
                    panel.rotation.x = -0.35;
                    panel.userData.adjustable = true;
                    add(panel, `Panel ${r * cols + c + 1}`);
                    panelMat.push(mat);
                    panels.push(panel);
                }
            }
            const total = panels.length;
            let lastLit = -1;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const lit = Math.round(pct * total);
                    if (lit !== lastLit) {
                        panelMat.forEach((m, i) => {
                            const on = i < lit;
                            m.color.copy(on ? color : new THREE.Color(TRACK_COLOR));
                            m.emissive.copy(on ? color : new THREE.Color(0x000000)).multiplyScalar(on ? 0.25 : 0);
                        });
                        lastLit = lit;
                    }
                },
            };
        }

        case 'radar3d': {
            const disc = new THREE.Mesh(
                new THREE.CircleGeometry(1.5, 48),
                new THREE.MeshBasicMaterial({ color: 0x0f172a, transparent: true, opacity: 0.25, side: THREE.DoubleSide }),
            );
            disc.rotation.x = -Math.PI / 2;
            disc.position.y = -0.4;
            add(disc);
            for (let r = 1; r <= 3; r += 1) {
                const ring = new THREE.Mesh(
                    new THREE.RingGeometry(r * 0.5 - 0.01, r * 0.5, 48),
                    new THREE.MeshBasicMaterial({ color: 0x475569, transparent: true, opacity: 0.5, side: THREE.DoubleSide }),
                );
                ring.rotation.x = -Math.PI / 2;
                ring.position.y = -0.39;
                add(ring);
            }
            const sweepGeom = new THREE.CircleGeometry(1.5, 32, 0, Math.PI / 6);
            const sweep = new THREE.Mesh(sweepGeom, new THREE.MeshBasicMaterial({ color: 0x4318ff, transparent: true, opacity: 0.35, side: THREE.DoubleSide }));
            sweep.rotation.x = -Math.PI / 2;
            sweep.position.y = -0.38;
            sweep.userData.adjustable = true;
            add(sweep, '');
            const blipCount = 8;
            const blips = Array.from({ length: blipCount }).map((_, i) => {
                const angle = (i / blipCount) * Math.PI * 2 + i * 0.37;
                const radius = 0.35 + (i % 3) * 0.35;
                const blip = new THREE.Mesh(new THREE.SphereGeometry(0.06, 10, 10), standard(0x4318ff, { emissive: 0x000000 }));
                blip.position.set(Math.cos(angle) * radius, -0.35, Math.sin(angle) * radius);
                blip.userData.angle = angle;
                add(blip, `Node ${i + 1}`);
                return blip;
            });
            let lastActive = -1;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    sweep.rotation.z = -(t * 1.4);
                    sweep.material.color.copy(color);
                    const activeCount = Math.max(1, Math.round(pct * blipCount));
                    if (activeCount !== lastActive) {
                        blips.forEach((b, i) => {
                            const on = i < activeCount;
                            b.material.color.copy(on ? color : new THREE.Color(0x475569));
                            b.material.emissive.copy(on ? color : new THREE.Color(0x000000)).multiplyScalar(on ? 0.4 : 0);
                        });
                        lastActive = activeCount;
                    }
                },
            };
        }

        case 'orb3d': {
            const shell = new THREE.Mesh(
                new THREE.IcosahedronGeometry(1.3, 1),
                new THREE.MeshBasicMaterial({ color: 0xd1d5db, wireframe: true, transparent: true, opacity: 0.45 }),
            );
            add(shell);
            const core = new THREE.Mesh(
                new THREE.SphereGeometry(0.6, 24, 24),
                standard(0x4318ff, { emissive: 0x000000, transparent: true, opacity: 0.9 }),
            );
            core.userData.adjustable = true;
            add(core, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    const pulse = 1 + Math.sin(t * (1.5 + pct * 3)) * (0.06 + pct * 0.1);
                    const s = (0.5 + pct * 0.7) * pulse;
                    core.scale.setScalar(s);
                    core.material.color.copy(color);
                    core.material.emissive.copy(color).multiplyScalar(0.4 + pct * 0.4);
                    shell.rotation.y = t * 0.25;
                    dataMeshes[0].label = `${Math.round(pct * 100)}% intensity`;
                },
            };
        }

        case 'network3d': {
            const nodeCount = 6;
            const hub = new THREE.Mesh(new THREE.SphereGeometry(0.22, 16, 16), standard(0x334155, { emissive: 0x000000 }));
            hub.userData.adjustable = true;
            add(hub, '');
            const edgeMat = new THREE.LineBasicMaterial({ color: 0x94a3b8, transparent: true, opacity: 0.5 });
            const nodes = [];
            for (let i = 0; i < nodeCount; i += 1) {
                const angle = (i / nodeCount) * Math.PI * 2;
                const radius = 1.6;
                const baseY = Math.sin(i * 1.3) * 0.4;
                const pos = new THREE.Vector3(Math.cos(angle) * radius, baseY, Math.sin(angle) * radius);
                const node = new THREE.Mesh(new THREE.SphereGeometry(0.13, 14, 14), standard(TRACK_COLOR, { emissive: 0x000000 }));
                node.position.copy(pos);
                node.userData.baseY = baseY;
                add(node, `Meter ${i + 1}`);
                nodes.push(node);
                const edgeGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), pos]);
                add(new THREE.Line(edgeGeom, edgeMat));
            }
            let lastActive = -1;
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    hub.material.color.copy(color);
                    hub.material.emissive.copy(color).multiplyScalar(0.3 + Math.sin(t * 2) * 0.1);
                    const active = Math.max(1, Math.round(pct * nodeCount));
                    if (active !== lastActive) {
                        nodes.forEach((n, i) => {
                            const on = i < active;
                            n.material.color.copy(on ? color : new THREE.Color(TRACK_COLOR));
                            n.material.emissive.copy(on ? color : new THREE.Color(0x000000)).multiplyScalar(on ? 0.35 : 0);
                        });
                        lastActive = active;
                    }
                    nodes.forEach((n, i) => {
                        n.position.y = n.userData.baseY + Math.sin(t * 1.5 + i) * 0.06;
                    });
                    dataMeshes[0].label = `${active}/${nodeCount} online`;
                },
            };
        }

        case 'flow3d': {
            const start = new THREE.Vector3(-1.6, -0.2, 0);
            const end = new THREE.Vector3(1.6, -0.2, 0);
            const curve = new THREE.CatmullRomCurve3([start, new THREE.Vector3(0, 0.5, 0), end]);
            add(new THREE.Mesh(
                new THREE.TubeGeometry(curve, 40, 0.18, 12, false),
                new THREE.MeshStandardMaterial({ color: 0xd1d5db, transparent: true, opacity: 0.35 }),
            ));
            const sourceNode = new THREE.Mesh(new THREE.SphereGeometry(0.32, 16, 16), standard(0x22c55e));
            sourceNode.position.copy(start);
            sourceNode.userData.adjustable = true;
            add(sourceNode, 'Source');
            const sourceEntry = dataMeshes[dataMeshes.length - 1];
            const loadNode = new THREE.Mesh(new THREE.SphereGeometry(0.32, 16, 16), standard(0x334155));
            loadNode.position.copy(end);
            loadNode.userData.adjustable = true;
            add(loadNode, 'Load');
            const loadEntry = dataMeshes[dataMeshes.length - 1];
            const particleCount = 6;
            const particleMat = standard(0x4318ff, { emissive: 0x000000 });
            const particles = Array.from({ length: particleCount }).map((_, i) => {
                const p = new THREE.Mesh(new THREE.SphereGeometry(0.08, 10, 10), particleMat);
                add(p);
                p.userData.offset = i / particleCount;
                return p;
            });
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    particleMat.color.copy(color);
                    particleMat.emissive.copy(color).multiplyScalar(0.4);
                    const speed = 0.08 + pct * 0.35;
                    particles.forEach((p) => {
                        const u = (p.userData.offset + t * speed) % 1;
                        curve.getPointAt(u, p.position);
                    });
                    sourceEntry.label = `Source: ${Math.round(pct * 100)}%`;
                    loadEntry.label = `Load: ${Math.round(pct * 100)}%`;
                },
            };
        }

        case 'meter-grid3d': {
            const cols = 3;
            const rows = 2;
            const tanks = [];
            for (let r = 0; r < rows; r += 1) {
                for (let c = 0; c < cols; c += 1) {
                    const x = (c - (cols - 1) / 2) * 0.95;
                    const z = (r - (rows - 1) / 2) * 0.95;
                    const shell = new THREE.Mesh(
                        new THREE.CylinderGeometry(0.35, 0.35, 1.6, 20, 1, true),
                        new THREE.MeshBasicMaterial({ color: 0xd1d5db, side: THREE.DoubleSide, transparent: true, opacity: 0.3 }),
                    );
                    shell.position.set(x, -0.3, z);
                    add(shell);
                    const fill = new THREE.Mesh(pivotBottom(new THREE.CylinderGeometry(0.3, 0.3, 1, 20)), standard(0x4318ff));
                    fill.position.set(x, -1.1, z);
                    fill.userData.adjustable = true;
                    add(fill, `Tank ${r * cols + c + 1}`);
                    tanks.push({ fill, phase: (r * cols + c) * 0.6 });
                }
            }
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    tanks.forEach(({ fill, phase }, i) => {
                        const local = Math.max(0.05, Math.min(1, pct + Math.sin(phase) * 0.12));
                        fill.scale.y = local * 1.6;
                        fill.material.color.copy(color);
                        dataMeshes[i].label = `Tank ${i + 1}: ${Math.round(local * 100)}%`;
                    });
                },
            };
        }

        case 'cylinder3d':
        default: {
            add(new THREE.Mesh(
                new THREE.CylinderGeometry(1, 1, 3, 32, 1, true),
                new THREE.MeshStandardMaterial({ color: 0xd1d5db, side: THREE.DoubleSide, transparent: true, opacity: 0.35 }),
            ));
            const fluid = new THREE.Mesh(pivotBottom(new THREE.CylinderGeometry(0.95, 0.95, 1, 32)), standard(0x4318ff));
            fluid.position.y = -1.5;
            fluid.userData.adjustable = true;
            add(fluid, '');
            return {
                allMeshes, dataMeshes,
                frame: (t, pct, color) => {
                    fluid.scale.y = Math.max(0.05, pct * 3);
                    fluid.material.color.copy(color);
                    dataMeshes[0].label = `${Math.round(pct * 100)}%`;
                },
            };
        }
    }
}

const MAX_CONCURRENT_PREVIEW_CONTEXTS = 6;
let activePreviewContexts = 0;
const previewSlotQueue = [];

function acquirePreviewSlot() {
    if (activePreviewContexts < MAX_CONCURRENT_PREVIEW_CONTEXTS) {
        activePreviewContexts += 1;
        return Promise.resolve();
    }
    return new Promise((resolve) => previewSlotQueue.push(resolve));
}

function releasePreviewSlot() {
    activePreviewContexts = Math.max(0, activePreviewContexts - 1);
    const next = previewSlotQueue.shift();
    if (next) {
        activePreviewContexts += 1;
        next();
    }
}

const ThreeDWidget = ({ config }) => {
    const mountRef = useRef(null);
    const tooltipRef = useRef(null);
    const { settings = {}, variant = 'ring3d', title } = config;
    const accent = settings.color || '#4318FF';
    const max = settings.max ?? 100;
    const data = settings.data;
    const isPreview = typeof config.id === 'string' && config.id.startsWith('preview-');
    const [inViewRef, inView] = useInView('400px 0px', undefined);
    const report = useMeterField(title, { meterId: settings.meterId, skip: isPreview });
    const rawLiveValue = useLiveValue(settings.value ?? 62, {
        id: config.id, endpoint: report.field ? 'report' : '', min: 0, max,
    });
    const liveValue = report.field ? (report.latest ?? 0) : rawLiveValue;
    const hasHud = SINGLE_VALUE_VARIANTS.has(variant);
    const percent = max > 0 ? Math.min(100, Math.max(0, Math.round((liveValue / max) * 100))) : 0;

    const targetPctRef = useRef(max > 0 ? liveValue / max : 0);
    const colorRef = useRef(new THREE.Color(accent));

    const isDraggingRef = useRef(false);
    const controlsRef = useRef(null);
    const cameraRef = useRef(null);
    const autoRotateWantedRef = useRef(true);
    const [rotating, setRotating] = useState(true);
    useEffect(() => {
        if (isDraggingRef.current) return;
        targetPctRef.current = max > 0 ? Math.min(1, Math.max(0, liveValue / max)) : 0;
    }, [liveValue, max]);
    useEffect(() => {
        colorRef.current.set(accent);
    }, [accent]);
    const [snapshot, setSnapshot] = useState(null);
    const [webglError, setWebglError] = useState(false);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return undefined;
        if (!isPreview && !inView) return undefined;

        if (isPreview) {
            let cancelled = false;
            acquirePreviewSlot().then(() => {
                if (cancelled) { releasePreviewSlot(); return; }
                const width = mount.clientWidth || 200;
                const height = mount.clientHeight || 160;

                const scene = new THREE.Scene();
                const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
                camera.position.set(0, 1.2, 5);
                camera.lookAt(0, 0, 0);

                let renderer;
                try {
                    renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, preserveDrawingBuffer: true });
                } catch (err) {
                    releasePreviewSlot();
                    return;
                }
                renderer.setSize(width, height);
                renderer.setPixelRatio(1);

                scene.add(new THREE.AmbientLight(0xffffff, 0.6));
                const dirLight = new THREE.DirectionalLight(0xffffff, 0.9);
                dirLight.position.set(3, 5, 4);
                scene.add(dirLight);

                const { allMeshes, frame } = buildScene(variant, scene, { data, unit: settings.unit });
                frame(0, targetPctRef.current, colorRef.current);
                renderer.render(scene, camera);
                const dataUrl = renderer.domElement.toDataURL('image/png');

                allMeshes.forEach((m) => { m.geometry.dispose(); m.material.dispose(); });
                renderer.dispose();
                releasePreviewSlot();

                if (!cancelled) setSnapshot(dataUrl);
            });
            return () => { cancelled = true; };
        }

        const width = mount.clientWidth || 200;
        const height = mount.clientHeight || 160;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
        camera.position.set(0, 1.2, 5);
        camera.lookAt(0, 0, 0);

        let renderer;
        try {
            renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        } catch (err) {
            setWebglError(true);
            return undefined;
        }
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.domElement.style.touchAction = 'none';
        mount.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 0.6));
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.9);
        dirLight.position.set(3, 5, 4);
        scene.add(dirLight);

        const { allMeshes, dataMeshes, frame } = buildScene(variant, scene, { data, unit: settings.unit });

        let controls = null;
        let idleTimer = null;
        let handlePointerMove;
        let handlePointerLeave;
        let handlePointerDown;
        let handlePointerUp;
        let handleClick;
        let handleDblClick;
        let hovered = null;
        let dragging = null;
        const pulses = [];

        if (!isPreview) {
            controls = new OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.dampingFactor = 0.08;
            controls.enablePan = false;
            controls.minDistance = 2.5;
            controls.maxDistance = 8;
            controls.autoRotate = autoRotateWantedRef.current;
            controls.autoRotateSpeed = 1.4;
            controls.addEventListener('start', () => {
                controls.autoRotate = false;
                if (idleTimer) clearTimeout(idleTimer);
            });
            controls.addEventListener('end', () => {
                idleTimer = setTimeout(() => { controls.autoRotate = autoRotateWantedRef.current; }, 2200);
            });
            controlsRef.current = controls;
            cameraRef.current = camera;

            const raycaster = new THREE.Raycaster();
            const pointerNdc = new THREE.Vector2();
            const setHover = (entry) => {
                if (hovered === entry) return;
                if (hovered) {
                    hovered.mesh.material.emissive?.copy(hovered.baseEmissive || new THREE.Color(0x000000));
                }
                hovered = entry;
                if (hovered && hovered.mesh.material.emissive) {
                    hovered.baseEmissive = hovered.mesh.material.emissive.clone();
                    hovered.mesh.material.emissive.setHex(0x475569);
                }
                if (!dragging) {
                    renderer.domElement.style.cursor = entry ? (entry.mesh.userData.adjustable ? 'ns-resize' : 'pointer') : 'grab';
                }
            };
            const raycastAt = (e) => {
                const rect = renderer.domElement.getBoundingClientRect();
                pointerNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
                pointerNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
                raycaster.setFromCamera(pointerNdc, camera);
                const hits = raycaster.intersectObjects(dataMeshes.map((d) => d.mesh), false);
                return { rect, hit: hits.length ? dataMeshes.find((d) => d.mesh === hits[0].object) : null };
            };
            const showTooltip = (rect, e, text) => {
                if (!tooltipRef.current) return;
                tooltipRef.current.textContent = text;
                tooltipRef.current.style.left = `${e.clientX - rect.left + 12}px`;
                tooltipRef.current.style.top = `${e.clientY - rect.top + 8}px`;
                tooltipRef.current.style.opacity = '1';
            };
            const hideTooltip = () => {
                if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
            };

            handlePointerDown = (e) => {
                const { rect, hit } = raycastAt(e);
                if (!hit || !hit.mesh.userData.adjustable) return;
                dragging = { startClientY: e.clientY, startPct: targetPctRef.current, rectHeight: rect.height };
                isDraggingRef.current = true;
                controls.autoRotate = false;
                controls.enabled = false;
                renderer.domElement.style.cursor = 'ns-resize';
                renderer.domElement.setPointerCapture?.(e.pointerId);
            };

            handlePointerMove = (e) => {
                if (dragging) {
                    const deltaPx = dragging.startClientY - e.clientY;
                    const deltaPct = deltaPx / Math.max(120, dragging.rectHeight * 0.9);
                    targetPctRef.current = Math.min(1, Math.max(0, dragging.startPct + deltaPct));
                    const rect = renderer.domElement.getBoundingClientRect();
                    const shownValue = Math.round(targetPctRef.current * max);
                    showTooltip(rect, e, `${shownValue}${settings.unit ? ` ${settings.unit}` : ''} (${Math.round(targetPctRef.current * 100)}%)`);
                    return;
                }
                const { rect, hit } = raycastAt(e);
                setHover(hit);
                if (hit && hit.label) {
                    const hint = hit.mesh.userData.adjustable ? `${hit.label || ''} · drag to adjust`.trim() : hit.label;
                    showTooltip(rect, e, hint);
                } else {
                    hideTooltip();
                }
            };
            handlePointerUp = (e) => {
                if (!dragging) return;
                dragging = null;
                isDraggingRef.current = false;
                controls.enabled = true;
                controls.autoRotate = false;
                if (idleTimer) clearTimeout(idleTimer);
                idleTimer = setTimeout(() => { controls.autoRotate = autoRotateWantedRef.current; }, 2200);
                renderer.domElement.releasePointerCapture?.(e.pointerId);
                hideTooltip();
            };
            handlePointerLeave = () => {
                if (dragging) return;
                setHover(null);
                hideTooltip();
            };
            handleClick = () => {
                if (!hovered) return;
                pulses.push({ mesh: hovered.mesh, start: performance.now() });
            };
            handleDblClick = () => {
                camera.position.set(0, 1.2, 5);
                controls.target.set(0, 0, 0);
                controls.update();
            };

            renderer.domElement.style.cursor = 'grab';
            renderer.domElement.addEventListener('pointerdown', handlePointerDown);
            renderer.domElement.addEventListener('pointermove', handlePointerMove);
            renderer.domElement.addEventListener('pointerup', handlePointerUp);
            renderer.domElement.addEventListener('pointerleave', handlePointerLeave);
            renderer.domElement.addEventListener('click', handleClick);
            renderer.domElement.addEventListener('dblclick', handleDblClick);
        }

        const clock = new THREE.Clock();
        let currentPct = targetPctRef.current;
        let animT = 0;
        let frameId;
        const animate = () => {
            if (controls) {
                controls.update();
            } else {
                scene.rotation.y += 0.004;
            }
            const dt = clock.getDelta();
            if (!isPreview && autoRotateWantedRef.current) {
                animT += dt;
            }
            const t = isPreview ? 0 : animT;

            if (dragging) {
                currentPct = targetPctRef.current;
            } else if (Math.abs(targetPctRef.current - currentPct) > PCT_EPSILON) {
                currentPct += (targetPctRef.current - currentPct) * PCT_LERP;
            } else {
                currentPct = targetPctRef.current;
            }
            frame(t, currentPct, colorRef.current);

            if (pulses.length) {
                const now = performance.now();
                for (let i = pulses.length - 1; i >= 0; i -= 1) {
                    const p = pulses[i];
                    const progress = (now - p.start) / 320;
                    if (progress >= 1) {
                        p.mesh.scale.x = p.mesh.userData.baseScaleX ?? p.mesh.scale.x;
                        p.mesh.scale.z = p.mesh.userData.baseScaleZ ?? p.mesh.scale.z;
                        pulses.splice(i, 1);
                    } else {
                        if (p.mesh.userData.baseScaleX === undefined) {
                            p.mesh.userData.baseScaleX = p.mesh.scale.x;
                            p.mesh.userData.baseScaleZ = p.mesh.scale.z;
                        }
                        const bump = 1 + Math.sin(progress * Math.PI) * 0.3;
                        p.mesh.scale.x = p.mesh.userData.baseScaleX * bump;
                        p.mesh.scale.z = p.mesh.userData.baseScaleZ * bump;
                    }
                }
            }

            renderer.render(scene, camera);
            frameId = requestAnimationFrame(animate);
        };
        animate();

        let resizeFrameId = null;
        const resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(resizeFrameId);
            resizeFrameId = requestAnimationFrame(() => {
                const w = mount.clientWidth || width;
                const h = mount.clientHeight || height;
                renderer.setSize(w, h);
                camera.aspect = w / h;
                camera.updateProjectionMatrix();
            });
        });
        resizeObserver.observe(mount);

        return () => {
            if (idleTimer) clearTimeout(idleTimer);
            cancelAnimationFrame(resizeFrameId);
            cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            isDraggingRef.current = false;
            if (controls) {
                renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
                renderer.domElement.removeEventListener('pointermove', handlePointerMove);
                renderer.domElement.removeEventListener('pointerup', handlePointerUp);
                renderer.domElement.removeEventListener('pointerleave', handlePointerLeave);
                renderer.domElement.removeEventListener('click', handleClick);
                renderer.domElement.removeEventListener('dblclick', handleDblClick);
                controls.dispose();
                if (controlsRef.current === controls) controlsRef.current = null;
                if (cameraRef.current === camera) cameraRef.current = null;
            }
            allMeshes.forEach((m) => { m.geometry.dispose(); m.material.dispose(); });
            renderer.dispose();
            if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
        };
    }, [variant, data, isPreview, inView]);

    const toggleRotate = () => {
        const next = !autoRotateWantedRef.current;
        autoRotateWantedRef.current = next;
        setRotating(next);
        if (controlsRef.current) controlsRef.current.autoRotate = next;
    };
    const resetView = () => {
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        if (!camera || !controls) return;
        camera.position.set(0, 1.2, 5);
        controls.target.set(0, 0, 0);
        controls.update();
    };

    if (webglError && !isPreview) {
        return (
            <div
                className="threed-widget"
                ref={inViewRef}
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    height: '100%', gap: 8, color: '#9ca3af', textAlign: 'center', padding: 12,
                }}
            >
                <i className="fas fa-cube" style={{ fontSize: 22 }}></i>
            </div>
        );
    }

    return (
        <div className="threed-widget" ref={inViewRef}>
            <div className="threed-widget__stage" ref={mountRef}>
                {isPreview && snapshot && <img className="threed-widget__snapshot" src={snapshot} alt="" />}
            </div>
            <div className="threed-widget__tooltip" ref={tooltipRef} />
            {hasHud && (
                <div className="threed-widget__hud">
                    <span className="threed-widget__hud-value" style={{ fontSize: settings.fontSize ? `${settings.fontSize}px` : undefined, color: settings.textColor || undefined }}>
                        {liveValue}{settings.unit ? ` ${settings.unit}` : ''}
                    </span>
                    <span className="threed-widget__hud-percent" style={{ fontSize: settings.fontSize ? `${settings.fontSize * 0.5}px` : undefined }}>{percent}%</span>
                </div>
            )}
            {!isPreview && (
                <>
                    <div className="threed-widget__hint"><i className="fas fa-arrows-rotate"></i> drag · scroll to zoom</div>
                    <div className="threed-widget__controls">
                        <button
                            type="button"
                            className="threed-widget__ctrl-btn"
                            title={rotating ? 'Pause rotation' : 'Resume rotation'}
                            onClick={toggleRotate}
                        >
                            <i className={`fas fa-${rotating ? 'pause' : 'play'}`}></i>
                        </button>
                        <button type="button" className="threed-widget__ctrl-btn" title="Reset view" onClick={resetView}>
                            <i className="fas fa-camera-rotate"></i>
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ThreeDWidget;
